/**
 * StrutturaFormPage Component - Hedita Design System
 * Feature: Strutture - Form creazione/modifica struttura
 * Layout: Pagina intera (stile identico a form prenotazioni)
 */

import { useState, useEffect } from "react";
import { ArrowLeft, CheckCircle, AlertCircle, Home, MapPin, Users, BedDouble, Phone, Mail, Globe, Calendar, Settings, Save, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useStruttura, useCreateStruttura, useUpdateStruttura } from "@/features/strutture/hooks";
import { useNavigate, useParams } from "react-router-dom";
import type { Struttura } from "@/types";

const TIPOLOGIE_STRUTTURE = ["Villa", "Appartamento", "B&B", "Masseria", "Trullo", "Hotel", "Casa vacanze", "Agriturismo"];
const STATI_STRUTTURE = ["Attiva", "Inattiva", "In manutenzione"];
const PARCHEGGIO = ["Nessuno", "Gratuito", "A pagamento", "Privato", "Struttura"];

export default function StrutturaFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "nuovo";
  
  const { data: struttura, isLoading } = useStruttura(isNew ? undefined : id);
  const createMut = useCreateStruttura();
  const updateMut = useUpdateStruttura();

  const [form, setForm] = useState({
    nome: "",
    slogan: "",
    tipo: "Appartamento",
    stato: "Attiva",
    paese: "Italia",
    visibilita: "Attiva",
    regione: "",
    citta: "",
    indirizzo: "",
    piano: "",
    num_camere: 1,
    max_ospiti: 2,
    superficie: 0,
    parcheggio: "Nessuno",
    link_prenotazione: "",
    descrizione: "",
    telefono: "",
    email: "",
    provincia: "",
    sito_web: "",
    posti_letto_totali: 2,
    check_in_time: "15:00",
    check_out_time: "10:00",
    data_apertura: "",
    note: "",
    cin: "",
    pet_friendly: false,
    host_assegnato: "",
    host_id: "",
    foto_principale: "",
    logo: "",
    servizi: "[]",
    istruzioni_checkin: "",
    istruzioni_checkout: "",
    regole_casa: "",
    dotazioni: "",
    info_private_extra: "[]",
    cose_da_fare: "",
    dove_mangiare: "",
    trasporti_zona: "",
    parcheggio_note: "",
    da_sapere_extra: "[]",
    descrizione_posizione: "",
    latitudine: "",
    longitudine: "",
    mappa_embed: "",
  });
  
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (struttura) {
      setForm({
        nome: struttura.nome ?? "",
        slogan: struttura.slogan ?? "",
        tipo: struttura.tipo ?? "Appartamento",
        stato: struttura.stato ?? "Attiva",
        paese: struttura.paese ?? "Italia",
        visibilita: struttura.visibilita ?? "Attiva",
        regione: struttura.regione ?? "",
        citta: struttura.citta ?? "",
        indirizzo: struttura.indirizzo ?? "",
        piano: struttura.piano ?? "",
        num_camere: struttura.num_camere ?? 1,
        max_ospiti: struttura.max_ospiti ?? 2,
        superficie: struttura.superficie ?? 0,
        parcheggio: struttura.parcheggio ?? "Nessuno",
        link_prenotazione: struttura.link_prenotazione ?? "",
        descrizione: struttura.descrizione ?? "",
        telefono: struttura.telefono ?? "",
        email: struttura.email ?? "",
        provincia: struttura.provincia ?? "",
        sito_web: struttura.sito_web ?? "",
        posti_letto_totali: struttura.posti_letto_totali ?? 2,
        check_in_time: struttura.check_in_time ?? "15:00",
        check_out_time: struttura.check_out_time ?? "10:00",
        data_apertura: struttura.data_apertura ?? "",
        note: struttura.note ?? "",
        cin: struttura.cin ?? "",
        pet_friendly: struttura.pet_friendly ?? false,
        host_assegnato: struttura.host_assegnato ?? "",
        host_id: struttura.host_id ?? "",
        foto_principale: struttura.foto_principale ?? "",
        logo: struttura.logo ?? "",
        servizi: struttura.servizi ?? "[]",
        istruzioni_checkin: struttura.istruzioni_checkin ?? "",
        istruzioni_checkout: struttura.istruzioni_checkout ?? "",
        regole_casa: struttura.regole_casa ?? "",
        dotazioni: struttura.dotazioni ?? "",
        info_private_extra: struttura.info_private_extra ?? "[]",
        cose_da_fare: struttura.cose_da_fare ?? "",
        dove_mangiare: struttura.dove_mangiare ?? "",
        trasporti_zona: struttura.trasporti_zona ?? "",
        parcheggio_note: struttura.parcheggio_note ?? "",
        da_sapere_extra: struttura.da_sapere_extra ?? "[]",
        descrizione_posizione: struttura.descrizione_posizione ?? "",
        latitudine: struttura.latitudine ?? "",
        longitudine: struttura.longitudine ?? "",
        mappa_embed: struttura.mappa_embed ?? "",
      });
    }
  }, [struttura]);

  const set = (k: keyof typeof form, v: string | number | boolean) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    if (!form.nome.trim()) return;
    setSaveStatus("saving");
    setSaveError(null);

    const data = {
      ...form,
      num_camere: Number(form.num_camere) || 1,
      max_ospiti: Number(form.max_ospiti) || 2,
      superficie: Number(form.superficie) || 0,
      posti_letto_totali: Number(form.posti_letto_totali) || 2,
    };

    const onSuccess = (res: Struttura) => {
      setSaveStatus("saved");
      setTimeout(() => {
        setSaveStatus("idle");
        navigate("/strutture/lista");
      }, 1500);
    };

    const onError = (error: any) => {
      setSaveStatus("error");
      setSaveError(error.message || "Errore durante il salvataggio");
    };

    if (isNew) {
      createMut.mutate(data, { onSuccess, onError });
    } else {
      updateMut.mutate({ id: id!, data }, { onSuccess, onError });
    }
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto p-6 space-y-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <Skeleton className="h-8 w-48" />
          </div>
          <div className="space-y-4">
            {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/strutture/lista")}
                className="p-2 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-foreground">
                  {isNew ? "Nuova Struttura" : "Modifica Struttura"}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {isNew ? "Aggiungi una nuova struttura al sistema" : "Modifica i dettagli della struttura"}
                </p>
              </div>
            </div>
            <button
              onClick={handleSave}
              disabled={saveStatus === "saving" || !form.nome.trim()}
              className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-primary/90 transition-all disabled:opacity-50"
            >
              {saveStatus === "saving" ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground/20 border-t-primary-foreground" />
                  Salvataggio...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {isNew ? "Crea" : "Salva"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {saveStatus === "error" && (
          <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4 flex items-center gap-3">
            <AlertCircle className="h-4 w-4 text-rose-400" />
            <span className="text-sm text-rose-400">{saveError}</span>
          </div>
        )}

        {saveStatus === "saved" && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 flex items-center gap-3">
            <CheckCircle className="h-4 w-4 text-emerald-400" />
            <span className="text-sm text-emerald-400">Struttura salvata con successo!</span>
          </div>
        )}

        {/* Informazioni di Base */}
        <div className="bg-secondary/30 rounded-xl p-6 space-y-4 border border-border/50">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-4">
            <Home className="h-4 w-4 text-primary" />
            Informazioni di base
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Nome *</label>
              <input 
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.nome}
                onChange={e => set("nome", e.target.value)}
                placeholder="Nome della struttura"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Slogan</label>
              <input 
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.slogan}
                onChange={e => set("slogan", e.target.value)}
                placeholder="Slogan della struttura"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Tipologia</label>
              <select 
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.tipo}
                onChange={e => set("tipo", e.target.value)}
              >
                {TIPOLOGIE_STRUTTURE.map(tipo => (
                  <option key={tipo} value={tipo}>{tipo}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Stato</label>
              <select 
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.stato}
                onChange={e => set("stato", e.target.value)}
              >
                {STATI_STRUTTURE.map(stato => (
                  <option key={stato} value={stato}>{stato}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Paese</label>
              <input 
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.paese}
                onChange={e => set("paese", e.target.value)}
                placeholder="Italia"
              />
            </div>
          </div>
        </div>

        {/* Posizione */}
        <div className="bg-secondary/30 rounded-xl p-6 space-y-4 border border-border/50">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-4">
            <MapPin className="h-4 w-4 text-primary" />
            Posizione
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Regione</label>
              <input 
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.regione}
                onChange={e => set("regione", e.target.value)}
                placeholder="Regione"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Provincia</label>
              <input 
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.provincia}
                onChange={e => set("provincia", e.target.value)}
                placeholder="Provincia"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Città</label>
            <input 
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
              value={form.citta}
              onChange={e => set("citta", e.target.value)}
              placeholder="Città"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Indirizzo</label>
            <input 
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
              value={form.indirizzo}
              onChange={e => set("indirizzo", e.target.value)}
              placeholder="Indirizzo completo"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">CAP</label>
              <input 
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.cap}
                onChange={e => set("cap", e.target.value)}
                placeholder="CAP"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Piano</label>
              <input 
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.piano}
                onChange={e => set("piano", e.target.value)}
                placeholder="Piano terra, 1° piano, etc."
              />
            </div>
          </div>
        </div>

        {/* Capacità e Servizi */}
        <div className="bg-secondary/30 rounded-xl p-6 space-y-4 border border-border/50">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-4">
            <Users className="h-4 w-4 text-primary" />
            Capacità e servizi
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Numero camere</label>
              <input 
                type="number"
                min={1}
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.num_camere}
                onChange={e => set("num_camere", parseInt(e.target.value) || 1)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Ospiti max</label>
              <input 
                type="number"
                min={1}
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.max_ospiti}
                onChange={e => set("max_ospiti", parseInt(e.target.value) || 2)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Posti letto totali</label>
              <input 
                type="number"
                min={1}
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.posti_letto_totali}
                onChange={e => set("posti_letto_totali", parseInt(e.target.value) || 2)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Superficie (mq)</label>
              <input 
                type="number"
                min={0}
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.superficie}
                onChange={e => set("superficie", parseInt(e.target.value) || 0)}
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Parcheggio</label>
            <select 
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
              value={form.parcheggio}
              onChange={e => set("parcheggio", e.target.value)}
            >
              {PARCHEGGIO.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Contatti */}
        <div className="bg-secondary/30 rounded-xl p-6 space-y-4 border border-border/50">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-4">
            <Phone className="h-4 w-4 text-primary" />
            Contatti
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Telefono</label>
              <input 
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.telefono}
                onChange={e => set("telefono", e.target.value)}
                placeholder="+39 ..."
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Email</label>
              <input 
                type="email"
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.email}
                onChange={e => set("email", e.target.value)}
                placeholder="email@esempio.com"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Sito web</label>
            <input 
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
              value={form.sito_web}
              onChange={e => set("sito_web", e.target.value)}
              placeholder="https://www.esempio.com"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Link prenotazione</label>
            <input 
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
              value={form.link_prenotazione}
              onChange={e => set("link_prenotazione", e.target.value)}
              placeholder="Link diretto per prenotazioni"
            />
          </div>
        </div>

        {/* Orari */}
        <div className="bg-secondary/30 rounded-xl p-6 space-y-4 border border-border/50">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-4">
            <Calendar className="h-4 w-4 text-primary" />
            Orari
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Check-in</label>
              <input 
                type="time"
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.check_in_time}
                onChange={e => set("check_in_time", e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Check-out</label>
              <input 
                type="time"
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
                value={form.check_out_time}
                onChange={e => set("check_out_time", e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Data apertura</label>
            <input 
              type="date"
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50"
              value={form.data_apertura}
              onChange={e => set("data_apertura", e.target.value)}
            />
          </div>
        </div>

        {/* Descrizione */}
        <div className="bg-secondary/30 rounded-xl p-6 space-y-4 border border-border/50">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground mb-4">
            <Globe className="h-4 w-4 text-primary" />
            Descrizione
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Descrizione</label>
            <textarea 
              rows={4}
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50 resize-none"
              value={form.descrizione}
              onChange={e => set("descrizione", e.target.value)}
              placeholder="Descrizione della struttura..."
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Note</label>
            <textarea 
              rows={3}
              className="w-full px-3 py-2.5 rounded-lg border border-border bg-card text-sm focus:outline-none focus:border-primary/50 resize-none"
              value={form.note}
              onChange={e => set("note", e.target.value)}
              placeholder="Note aggiuntive..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}
